import { useState, useEffect, useRef, useCallback } from 'react'
import clsx from 'clsx'

// ── Types ────────────────────────────────────────────────────────────────────

type ConnectionState = 'connected' | 'reconnecting' | 'disconnected'

function resolveWebSocketUrl(): string | null {
  if (import.meta.env.VITE_WS_ENABLED === 'false') return null
  if (import.meta.env.VITE_WS_URL) return import.meta.env.VITE_WS_URL

  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws'
  return `${scheme}://${window.location.host}/ws`
}

const WS_URL = resolveWebSocketUrl()
const RECONNECT_DELAY_MS = 3000

// ── Config ───────────────────────────────────────────────────────────────────

const STATE_CONFIG: Record<
  ConnectionState,
  { dotClass: string; label: string; pulseClass: string }
> = {
  connected: {
    dotClass: 'bg-green-400',
    label: 'Connected',
    pulseClass: '',
  },
  reconnecting: {
    dotClass: 'bg-yellow-400',
    label: 'Reconnecting...',
    pulseClass: 'animate-pulse',
  },
  disconnected: {
    dotClass: 'bg-red-400',
    label: 'Disconnected',
    pulseClass: '',
  },
}

/**
 * ConnectionStatus -- small badge that reflects the current WebSocket
 * connection state.  Green = connected, yellow = reconnecting, red = disconnected.
 *
 * Uses its own lightweight WebSocket probe so it can work independently of
 * the main useWebSocket hook (avoids tight coupling).
 */
export default function ConnectionStatus() {
  const [state, setState] = useState<ConnectionState>('disconnected')
  const wsRef = useRef<WebSocket | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const unmountedRef = useRef(false)

  const connect = useCallback(() => {
    const url = WS_URL
    if (unmountedRef.current || !url) return

    setState('reconnecting')

    try {
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        if (!unmountedRef.current) setState('connected')
        if (timerRef.current) {
          clearTimeout(timerRef.current)
          timerRef.current = null
        }
      }

      ws.onclose = () => {
        wsRef.current = null
        if (!unmountedRef.current) {
          setState('disconnected')
          timerRef.current = setTimeout(() => {
            if (!unmountedRef.current) connect()
          }, RECONNECT_DELAY_MS)
        }
      }

      ws.onerror = () => {
        ws.close()
      }

      // We don't process messages here -- this is purely a status probe
      ws.onmessage = () => {}
    } catch {
      setState('disconnected')
    }
  }, [])

  useEffect(() => {
    unmountedRef.current = false
    connect()
    return () => {
      unmountedRef.current = true
      if (timerRef.current) clearTimeout(timerRef.current)
      wsRef.current?.close()
      wsRef.current = null
    }
  }, [connect])

  if (!WS_URL) return null

  const config = STATE_CONFIG[state]

  return (
    <div
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-slate-800/60 border border-surface-border text-xs text-slate-400"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <span
        className={clsx('w-2 h-2 rounded-full shrink-0', config.dotClass, config.pulseClass)}
        aria-hidden="true"
      />
      <span>{config.label}</span>
    </div>
  )
}
